import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { Pencil, Plus } from 'lucide-react';
import type { CrmAccountDetail, CrmActivity, CrmContact } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { t } from '@/i18n';
import { formatMoney } from '@/utils/currency';
import { ContractorLinkCard } from './ContractorLinkCard';

interface Props {
  nip: string;
  account: CrmAccountDetail;
  activities: CrmActivity[];
  note: string;
  onNoteChange: (value: string) => void;
  savingNote: boolean;
  onAddNote: () => void;
  onAddContact: () => void;
  onEditContact: (contact: CrmContact) => void;
  /** Refetch of the account + timeline (contractor link changes, notes…). */
  onChanged: () => void;
}

/**
 * "Przegląd" pane of the account page: deals, timeline + note composer,
 * contacts, the plain data card and the contractor link. Split out of
 * `AccountDetail`, which is now just the shell (header, tabs, dialogs).
 */
export function AccountOverviewTab({
  nip,
  account,
  activities,
  note,
  onNoteChange,
  savingNote,
  onAddNote,
  onAddContact,
  onEditContact,
  onChanged,
}: Props) {
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('crm.accounts.detail.deals')}</CardTitle>
          </CardHeader>
          <CardContent>
            {account.deals.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t('crm.accounts.detail.noDeals')}</p>
            ) : (
              <ul className="space-y-2">
                {account.deals.map((deal) => (
                  <li key={deal.id} className="flex items-center justify-between text-sm">
                    {/* Opens the deal sheet on the board via its `?deal=` deep link. */}
                    <Link to={`/${nip}/crm/szanse?deal=${deal.id}`} className="text-primary hover:underline">
                      {deal.name}
                    </Link>
                    <span className="flex items-center gap-3">
                      <Badge variant="outline">{deal.stageName}</Badge>
                      <span className="text-muted-foreground">
                        {deal.valueNet === null ? '—' : formatMoney(deal.valueNet, deal.currency, 2)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('crm.accounts.detail.timeline')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Textarea
                value={note}
                onChange={(event) => onNoteChange(event.target.value)}
                rows={2}
                placeholder={t('crm.accounts.detail.noteePlaceholder')}
              />
              <Button size="sm" onClick={onAddNote} disabled={note.trim() === '' || savingNote}>
                {t('crm.accounts.detail.addNote')}
              </Button>
            </div>

            {activities.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t('crm.accounts.detail.noActivity')}</p>
            ) : (
              <ul className="space-y-3">
                {activities.map((entry) => (
                  <li key={entry.id} className="border-l-2 pl-3 text-sm">
                    <div className="text-muted-foreground text-xs">
                      {dayjs(entry.occurredAt).format('DD.MM.YYYY HH:mm')} ·{' '}
                      {t(`crm.activityType.${entry.type}`)}
                      {entry.authorDisplayName ? ` · ${entry.authorDisplayName}` : ''}
                    </div>
                    {entry.subject ? <div className="font-medium">{entry.subject}</div> : null}
                    {entry.body ? <div className="whitespace-pre-wrap">{entry.body}</div> : null}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base">
              {t('crm.contacts.title')}
              {account.contacts.length > 0 ? (
                <span className="text-muted-foreground ml-2 font-normal">
                  {account.contacts.length}
                </span>
              ) : null}
            </CardTitle>
            <Button variant="outline" size="sm" onClick={onAddContact}>
              <Plus className="mr-1 h-4 w-4" />
              {t('crm.contacts.add')}
            </Button>
          </CardHeader>
          <CardContent>
            {account.contacts.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t('crm.contacts.empty')}</p>
            ) : (
              <ul className="space-y-3 text-sm">
                {account.contacts.map((contact) => (
                  <li key={contact.id} className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">
                          {[contact.firstName, contact.lastName].filter(Boolean).join(' ')}
                        </span>
                        {contact.isPrimary ? (
                          <Badge variant="secondary">{t('crm.contacts.primary')}</Badge>
                        ) : null}
                      </div>
                      {contact.jobTitle ? (
                        <div className="text-muted-foreground text-xs">{contact.jobTitle}</div>
                      ) : null}
                      {contact.email ? (
                        <a
                          href={`mailto:${contact.email}`}
                          className="text-primary block truncate text-xs hover:underline"
                        >
                          {contact.email}
                        </a>
                      ) : null}
                      {contact.phone ? (
                        <a
                          href={`tel:${contact.phone}`}
                          className="text-primary block text-xs hover:underline"
                        >
                          {contact.phone}
                        </a>
                      ) : null}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      aria-label={t('crm.contacts.edit')}
                      onClick={() => onEditContact(contact)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-1 pt-6 text-sm">
            {account.nip ? (
              <div>
                <span className="text-muted-foreground">NIP: </span>
                {account.nip}
              </div>
            ) : null}
            {account.website ? (
              <div>
                <span className="text-muted-foreground">WWW: </span>
                {account.website}
              </div>
            ) : null}
            {account.email ? (
              <div>
                <span className="text-muted-foreground">E-mail: </span>
                {account.email}
              </div>
            ) : null}
            {account.phone ? (
              <div>
                <span className="text-muted-foreground">Tel: </span>
                {account.phone}
              </div>
            ) : null}
            {account.emailDomains.length > 0 ? (
              <div>
                <span className="text-muted-foreground">
                  {t('crm.accounts.form.emailDomains')}:{' '}
                </span>
                {account.emailDomains.join(', ')}
              </div>
            ) : null}
            {account.ownerDisplayName ? (
              <div>
                <span className="text-muted-foreground">
                  {t('crm.accounts.columns.owner')}:{' '}
                </span>
                {account.ownerDisplayName}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <ContractorLinkCard nip={nip} account={account} onChanged={onChanged} />
      </div>
    </div>
  );
}
